/**
 * A ligação da campanha com o Meta Ads — e o quanto ela custa.
 *
 * Desde 05/10 (migration 0025) um bloco recebe gente de VÁRIAS campanhas da Meta: a gaveta
 * lista as vinculadas (cada uma com o gasto dela) e oferece a escolha a partir das campanhas
 * que a conta de anúncios já importou — o id digitado virou plano B.
 *
 * Três decisões de tela que valem o comentário:
 *
 *  - **A sugestão vem antes do campo.** Ninguém digita um id de 15 dígitos de cabeça; quem
 *    tentar vai copiar errado. O sistema já sabe de qual campanha do Meta vieram os cliques
 *    pagos deste link, então a tela oferece "parece ser esta — confirmar?" e deixa o campo
 *    como saída de emergência, não como caminho principal.
 *  - **Sugerir não é vincular, e a tela diz isso.** O botão é da pessoa. A contagem que
 *    sustenta a sugestão aparece ao lado (18 de 20 cliques = 90%) para que a confirmação seja
 *    uma decisão informada e não um "ok" automático.
 *  - **A exposição aparece aqui, em cima.** É a resposta do dono para "este grupo é crucial
 *    ou pode esperar?", e ela precisa ser conferível no mesmo lugar em que se declara o
 *    anúncio — senão a pessoa vincula e só descobre o efeito no próximo incidente.
 *
 *  - **Campanha ABO abre em conjuntos (0041).** Quem roda um conjunto por bloco (um criativo
 *    e um link por conjunto) liga o CONJUNTO, não a campanha. A árvore mostra campanha >
 *    conjuntos só na ABO (na CBO o orçamento é da campanha, e o servidor recusa), e trava o
 *    que daria dupla contagem: campanha inteira e conjunto dela nunca juntos.
 *
 * Gaveta e não página, como a de grupos: é ação de rotina feita a partir da lista, e a gaveta
 * `responsive` do design system já vira painel de baixo no celular (DESIGN_SYSTEM §11).
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  ErrorState,
  Field,
  Input,
  Spinner,
  formatCurrency,
} from '@mgdlead/ui';
import { Link2, Search, Unlink } from 'lucide-react';

import {
  MONEY_EXPOSURE,
  metaLinkSourceLabel,
  suggestionShareLabel,
} from '../../../features/campaign/campaign-ui';
import { matchesSearch } from '../../../features/group/group-search';
import { useCampaignMeta } from '../../../lib/api';
import type { Campaign } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

export interface CampaignMetaDrawerProps {
  workspaceId: string;
  campaign: Campaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canUpdate: boolean;
  pending: boolean;
  onLink: (externalIds: string[], source: 'MANUAL' | 'SUGGESTED') => void;
  onUnlink: (externalId: string) => void;
}

/** "R$ 347,31 em 7 dias" — ou o porquê de não haver número (sem métrica ≠ gastou zero). */
function gasto7d(c: { spendCents7d: number | null; currency: string | null }): string {
  if (c.spendCents7d === null) return 'sem gasto importado em 7 dias';
  return `${formatCurrency(c.spendCents7d / 100, c.currency ?? 'BRL')} em 7 dias`;
}

export function CampaignMetaDrawer({
  workspaceId,
  campaign,
  open,
  onOpenChange,
  canUpdate,
  pending,
  onLink,
  onUnlink,
}: CampaignMetaDrawerProps) {
  const campaignId = open && campaign ? campaign.id : null;
  const metaQuery = useCampaignMeta(workspaceId, campaignId);
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(new Set());
  const dados = metaQuery.data;

  /** As sugeridas pelos cliques, por id — para destacar na lista e carimbar `SUGGESTED`. */
  const sugeridas = useMemo(
    () => new Map((dados?.suggestion.suggestions ?? []).map((s) => [s.externalId, s])),
    [dados],
  );
  const vinculadasAqui = useMemo(
    () => new Set((dados?.links ?? []).map((l) => l.externalId)),
    [dados],
  );

  /** Os conjuntos importados de cada campanha (a árvore da ABO). */
  const conjuntosDe = useMemo(() => {
    const mapa = new Map<string, Opcao[]>();
    for (const o of dados?.available ?? []) {
      if (o.level !== 'ADSET' || o.parentExternalId === null) continue;
      mapa.set(o.parentExternalId, [...(mapa.get(o.parentExternalId) ?? []), o]);
    }
    return mapa;
  }, [dados]);

  /**
   * A lista de escolha: as campanhas importadas que ainda não estão neste bloco, mais as
   * sugeridas que a conta ainda não espelhou (sugestão de quem não conectou a conta). Cada
   * campanha ABO leva os conjuntos dela embaixo.
   */
  const opcoes = useMemo(() => {
    const importadas = (dados?.available ?? []).filter(
      (o) => o.level !== 'ADSET' && !vinculadasAqui.has(o.externalId),
    );
    const ids = new Set(importadas.map((o) => o.externalId));
    const soSugeridas: Opcao[] = (dados?.suggestion.suggestions ?? [])
      .filter((s) => !ids.has(s.externalId) && !vinculadasAqui.has(s.externalId))
      .map((s) => ({
        externalId: s.externalId,
        level: 'CAMPAIGN',
        parentExternalId: null,
        parentName: null,
        cbo: null,
        name: s.metaName,
        effectiveStatus: null,
        spendCents7d: null,
        currency: null,
        linkedTo: null,
      }));
    const casa = (o: Opcao) =>
      matchesSearch({ name: o.name, providerGroupId: o.externalId }, busca);
    return (
      [...importadas, ...soSugeridas]
        .map((campanha) => {
          const todos = campanha.cbo === false ? (conjuntosDe.get(campanha.externalId) ?? []) : [];
          // A campanha que casa com a busca leva todos os conjuntos; senão, só os que casam.
          const conjuntos = (casa(campanha) ? todos : todos.filter(casa)).filter(
            (c) => !vinculadasAqui.has(c.externalId),
          );
          return { campanha, conjuntos, aparece: casa(campanha) || conjuntos.length > 0 };
        })
        .filter((g) => g.aparece)
        // Sugeridas primeiro: são as que os cliques dizem que trazem gente para cá.
        .sort(
          (a, b) =>
            Number(sugeridas.has(b.campanha.externalId)) -
            Number(sugeridas.has(a.campanha.externalId)),
        )
    );
  }, [dados, vinculadasAqui, sugeridas, busca, conjuntosDe]);

  /**
   * O que daria dupla contagem fica travado na tela (o servidor recusa igual): a campanha com
   * conjunto ligado/marcado, e o conjunto cuja campanha está ligada/marcada.
   */
  function bloqueio(o: Opcao): string | null {
    if (o.linkedTo !== null) return `já está em “${o.linkedTo.name}”`;
    if (o.level === 'CAMPAIGN') {
      const conjuntos = conjuntosDe.get(o.externalId) ?? [];
      const ligado = conjuntos.find((c) => c.linkedTo !== null || vinculadasAqui.has(c.externalId));
      if (ligado) {
        const onde = ligado.linkedTo?.name ?? campaign?.name ?? 'outro bloco';
        return `tem conjunto ligado em “${onde}” — ligue por conjunto`;
      }
      if (conjuntos.some((c) => marcadas.has(c.externalId))) return 'conjunto dela marcado';
      return null;
    }
    const mae = (dados?.available ?? []).find((a) => a.externalId === o.parentExternalId);
    if (mae && (mae.linkedTo !== null || vinculadasAqui.has(mae.externalId))) {
      return 'a campanha inteira já está ligada';
    }
    if (o.parentExternalId !== null && marcadas.has(o.parentExternalId)) {
      return 'campanha inteira marcada';
    }
    return null;
  }

  function alternar(id: string) {
    setMarcadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  function vincularMarcadas() {
    // Um refetch pode travar o que já estava marcado (a mãe foi ligada noutro bloco): fica fora.
    const porId = new Map((dados?.available ?? []).map((a) => [a.externalId, a]));
    const ids = [...marcadas].filter((id) => {
      const o = porId.get(id);
      return o === undefined || bloqueio(o) === null;
    });
    // A procedência é por campanha: confirmar a sugestão ≠ escolher na lista.
    const doSugerido = ids.filter((id) => sugeridas.has(id));
    const escolhidas = ids.filter((id) => !sugeridas.has(id));
    if (doSugerido.length > 0) onLink(doSugerido, 'SUGGESTED');
    if (escolhidas.length > 0) onLink(escolhidas, 'MANUAL');
    setMarcadas(new Set());
  }

  const niveisMarcados = new Set(
    [...marcadas].map(
      (id) => (dados?.available ?? []).find((a) => a.externalId === id)?.level ?? 'CAMPAIGN',
    ),
  );
  const [umNivel] = niveisMarcados;
  const rotuloDoBotao =
    marcadas.size === 0
      ? 'Marque as campanhas para vincular'
      : niveisMarcados.size > 1
        ? `Vincular ${marcadas.size} itens`
        : umNivel === 'ADSET'
          ? marcadas.size === 1
            ? 'Vincular 1 conjunto'
            : `Vincular ${marcadas.size} conjuntos`
          : marcadas.size === 1
            ? 'Vincular 1 campanha'
            : `Vincular ${marcadas.size} campanhas`;

  return (
    <Drawer
      open={open}
      onOpenChange={(aberta) => {
        if (!aberta) {
          setDigitado('');
          setBusca('');
          setMarcadas(new Set());
        }
        onOpenChange(aberta);
      }}
      title={campaign ? `Meta Ads de ${campaign.name}` : 'Meta Ads da campanha'}
      description="As campanhas da Meta que mandam gente para este bloco. O painel soma o gasto delas e calcula quanto custou cada pessoa que entrou."
    >
      {campaign === null ? null : metaQuery.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : metaQuery.isError ? (
        <ErrorState
          title="Não conseguimos carregar a ligação com o Meta"
          description={humanizeError(metaQuery.error)}
          onRetry={() => void metaQuery.refetch()}
        />
      ) : dados === undefined ? null : (
        <div className="flex flex-col gap-4">
          {/* ---------------------- quanto está em jogo ---------------------- */}
          <div className="flex flex-col gap-2 border-b border-border pb-4">
            <span className="flex flex-wrap items-center gap-2">
              <Badge tone={MONEY_EXPOSURE[dados.exposure.level].tone} dot>
                {MONEY_EXPOSURE[dados.exposure.level].label}
              </Badge>
              {dados.exposure.paidClicks > 0 ? (
                <Badge tone="neutral" numeric>
                  {dados.exposure.paidClicks}{' '}
                  {dados.exposure.paidClicks === 1 ? 'clique pago' : 'cliques pagos'} em{' '}
                  {dados.exposure.windowHours}h
                </Badge>
              ) : null}
            </span>
            <p className="text-sm text-muted-foreground">
              {MONEY_EXPOSURE[dados.exposure.level].hint}
            </p>
          </div>

          {/* --------------------- as vinculadas a este bloco --------------------- */}
          <section className="flex flex-col gap-2" aria-label="Campanhas da Meta deste bloco">
            <span className="text-sm font-medium">
              Campanhas da Meta deste bloco ({dados.links.length})
            </span>
            {dados.links.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma ainda. Sem isso, o painel não sabe quanto você gastou para trazer gente para
                cá — e um grupo quebrado no meio de um anúncio ativo fica como aviso comum.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
                {dados.links.map((l) => (
                  <li key={l.externalId} className="flex items-start justify-between gap-2 p-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="break-words text-sm font-medium">
                        {l.name ?? <span className="font-mono">{l.externalId}</span>}
                      </span>
                      {l.level === 'ADSET' ? (
                        <span className="break-words text-2xs text-muted-foreground">
                          Conjunto da campanha {l.parentName ?? l.parentExternalId ?? '—'}
                        </span>
                      ) : null}
                      <span className="flex flex-wrap items-center gap-1 text-2xs text-muted-foreground">
                        {l.level === 'ADSET' ? <Badge tone="neutral">Conjunto</Badge> : null}
                        {l.effectiveStatus ? (
                          <Badge tone="outline">{l.effectiveStatus}</Badge>
                        ) : null}
                        <span>{gasto7d(l)}</span>
                        <span>
                          · {metaLinkSourceLabel(l.source)}
                          {l.linkedByName === null ? '' : ` por ${l.linkedByName}`}
                        </span>
                      </span>
                    </div>
                    {canUpdate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Unlink />}
                        disabled={pending}
                        aria-label={`Desvincular ${l.name ?? l.externalId}`}
                        onClick={() => onUnlink(l.externalId)}
                      >
                        Desvincular
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* --------------------------- escolher --------------------------- */}
          {canUpdate ? (
            <section
              className="flex flex-col gap-2 border-t border-border pt-4"
              aria-label="Escolher campanhas da Meta"
            >
              <span className="text-sm font-medium">Adicionar campanhas da Meta</span>
              {dados.available.length === 0 && sugeridas.size === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma campanha importada ainda. Conecte a conta de anúncios em{' '}
                  <strong>Meta Ads</strong> e use “Sincronizar agora”: as campanhas aparecem aqui
                  para você escolher.
                </p>
              ) : (
                <>
                  <Input
                    search
                    size="sm"
                    leadingIcon={<Search />}
                    placeholder="Buscar pelo nome da campanha"
                    aria-label="Buscar campanha da Meta"
                    value={busca}
                    onChange={(event) => setBusca(event.target.value)}
                  />
                  {opcoes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma campanha com esse nome.</p>
                  ) : (
                    <ul className="max-h-[40dvh] overflow-y-auto divide-y divide-border rounded-lg border border-border">
                      {opcoes.map(({ campanha, conjuntos }) => (
                        <li key={campanha.externalId} className="flex flex-col">
                          <OpcaoDaMeta
                            opcao={campanha}
                            sugestao={sugeridas.get(campanha.externalId)}
                            paidClicks={dados.suggestion.paidClicks}
                            marcada={marcadas.has(campanha.externalId)}
                            bloqueio={bloqueio(campanha)}
                            pending={pending}
                            onToggle={() => alternar(campanha.externalId)}
                            extra={
                              campanha.cbo === false && conjuntos.length > 0
                                ? `ABO · ${conjuntos.length} ${conjuntos.length === 1 ? 'conjunto' : 'conjuntos'} abaixo`
                                : null
                            }
                          />
                          {conjuntos.length > 0 ? (
                            <ul
                              className="flex flex-col border-t border-border bg-muted/40 pl-6"
                              aria-label={`Conjuntos de ${campanha.name ?? campanha.externalId}`}
                            >
                              {conjuntos.map((c) => (
                                <li key={c.externalId}>
                                  <OpcaoDaMeta
                                    opcao={c}
                                    sugestao={undefined}
                                    paidClicks={0}
                                    marcada={marcadas.has(c.externalId)}
                                    bloqueio={bloqueio(c)}
                                    pending={pending}
                                    onToggle={() => alternar(c.externalId)}
                                    extra="Conjunto"
                                  />
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Ação importante = largura cheia no celular (DESIGN_SYSTEM §11). */}
                  <Button
                    variant="primary"
                    leadingIcon={<Link2 />}
                    block
                    disabled={marcadas.size === 0 || pending}
                    onClick={vincularMarcadas}
                  >
                    {rotuloDoBotao}
                  </Button>
                </>
              )}

              {/* Plano B: a conta não está conectada, mas o id é conhecido. */}
              <details className="text-sm">
                <summary className="cursor-pointer text-2xs text-muted-foreground">
                  Não está na lista? Informe o id da campanha
                </summary>
                <div className="mt-2 flex flex-col gap-2">
                  <Field
                    label="Id da campanha no Meta"
                    hint="Só números. Ele aparece no Gerenciador de Anúncios, na coluna de identificação da campanha."
                  >
                    {(props) => (
                      <Input
                        {...props}
                        inputMode="numeric"
                        placeholder="23851234567890"
                        value={digitado}
                        onChange={(event) => setDigitado(event.target.value)}
                      />
                    )}
                  </Field>
                  <Button
                    variant="secondary"
                    disabled={digitado.trim().length === 0 || pending}
                    onClick={() => {
                      onLink([digitado.trim()], 'MANUAL');
                      setDigitado('');
                    }}
                  >
                    Vincular este id
                  </Button>
                </div>
              </details>
            </section>
          ) : null}

          <p className="text-2xs text-muted-foreground">
            Cada campanha da Meta fica em um bloco só — ou, se for ABO, cada conjunto dela em um
            bloco — assim o mesmo gasto nunca é contado duas vezes. Vincular não muda a
            distribuição: muda a conta do custo por entrada e a prioridade dos avisos.
          </p>
        </div>
      )}
    </Drawer>
  );
}

type Opcao = NonNullable<ReturnType<typeof useCampaignMeta>['data']>['available'][number];
type Sugestao = NonNullable<
  ReturnType<typeof useCampaignMeta>['data']
>['suggestion']['suggestions'][number];

/** Uma linha da escolha: campanha ou conjunto, com o porquê de estar travada. */
function OpcaoDaMeta({
  opcao,
  sugestao,
  paidClicks,
  marcada,
  bloqueio,
  pending,
  onToggle,
  extra,
}: {
  opcao: Opcao;
  sugestao: Sugestao | undefined;
  paidClicks: number;
  marcada: boolean;
  bloqueio: string | null;
  pending: boolean;
  onToggle: () => void;
  extra: string | null;
}) {
  return (
    <div className="flex flex-col gap-1 px-3 py-2">
      <Checkbox
        checked={marcada}
        disabled={bloqueio !== null || pending}
        onChange={onToggle}
        label={opcao.name ?? opcao.externalId}
      />
      <span className="flex flex-wrap items-center gap-1 pl-7 text-2xs text-muted-foreground">
        {sugestao ? (
          <Badge tone="success" dot>
            Sugerida: {sugestao.clicks} de {paidClicks} cliques (
            {suggestionShareLabel(sugestao.share)})
          </Badge>
        ) : null}
        {extra ? <Badge tone="neutral">{extra}</Badge> : null}
        {opcao.effectiveStatus ? <Badge tone="outline">{opcao.effectiveStatus}</Badge> : null}
        <span>{gasto7d(opcao)}</span>
        {bloqueio ? <span>· {bloqueio}</span> : null}
      </span>
    </div>
  );
}
